import { Button, Flex } from 'pink-lava-ui';
import { DataList } from '@/components/ui/list/DataList';

import type { Vehicle, VehicleListFilter, VehicleType, VehiclePayload } from '@dc-trans/data-layer';
import { vehicleSchema } from '@dc-trans/data-layer';
import { PlusIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormModal, useFormModal } from '@/components/ui/modal';
import { Tag } from 'antd';
import type { FormField } from '@/types/FieldSchema.type';
import { useNotification } from '@/context/notificationContext';
import dayjs from 'dayjs';
import { useUrlParamsContext } from '@/context/urlParamsContext';
import { useSelectOptionsStore } from '@/stores/selectOptionsStore';
import { useCreateVehicleMutation, useUpdateVehicleMutation } from '@/features/vehicle/api/mutations';
import queryClient from '@/libs/queryClient';
import { useVehicleList } from '@/features/vehicle/api/queries';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import type { ApiErrorNotificationMeta } from '@/context/apiErrorNotificationProvider';

const FORM_VEHICLE_DEFAULT_VALUE: Partial<VehiclePayload> = {
  license_plate_number: '',
  license_plate_prefix: '',
  license_plate_suffix: '',
  vehicle_type_ulid: undefined,
  cubication: undefined,
  kir_number: '',
  kir_expired_date: undefined,
  status: 'active',
};

const VehiclePage = () => {
  const { isMobile } = useBreakpoint();
  const { resetFilters, filters } = useUrlParamsContext();
  const showNotification = useNotification();
  const { visible, mode, showModal, hideModal } = useFormModal();
  const [selectedVehicle, setSelectedVehicle] = useState<Partial<VehiclePayload>>(FORM_VEHICLE_DEFAULT_VALUE);
  const { options } = useSelectOptionsStore();

  // Mutations
  const createMutation = useCreateVehicleMutation({
    meta: {
      message: () => {
        return `Gagal membuat data kendaraan.`;
      },
    } satisfies ApiErrorNotificationMeta,
  });
  const updateMutation = useUpdateVehicleMutation({
    meta: {
      message: () => {
        return `Gagal mengubah data kendaraan.`;
      },
    } satisfies ApiErrorNotificationMeta,
  });

  const baseVehicleTypesSelectProps: any = useMemo(
    () => ({
      apiUrl: 'vehicle-types',
      renderOption: (item: VehicleType) => ({
        value: item.ulid,
        label: item.name,
        subLabel: `(Kubikasi: ${Intl.NumberFormat('id-ID', { style: 'decimal' }).format(item.cubic_capacity)}m³)`,
        ...item,
      }),
      searchKey: 'name',
      apiParams: {
        page: 1,
        limit: 100,
      },
    }),
    []
  );
  const vehicleTypeFormSelectProps = useMemo(
    () => ({
      ...baseVehicleTypesSelectProps,
      cacheKey: 'vehicle-types-form',
    }),
    [baseVehicleTypesSelectProps]
  );
  const vehicleTypeFilterSelectProps = useMemo(
    () => ({
      ...baseVehicleTypesSelectProps,
      cacheKey: 'vehicle-types-filter',
    }),
    [baseVehicleTypesSelectProps]
  );

  // Form fields configuration
  const vehicleFields: FormField<VehiclePayload>[] = [
    {
      name: 'license_plate',
      type: 'combined-text',
      label: 'Nomor Polisi Kendaraaan',
      required: true,
      disabledInModes: ['update'],
      fields: [
        {
          name: 'license_plate_prefix',
          placeholder: 'D',
          required: true,
          maxLength: 2,
          width: '20%',
          onChange(value, setValue) {
            setValue('license_plate_prefix', value.toUpperCase());
          },
        },
        {
          name: 'license_plate_number',
          placeholder: '1234',
          required: true,
          maxLength: 4,
          width: '60%',
        },
        {
          name: 'license_plate_suffix',
          placeholder: 'XYZ',
          required: true,
          maxLength: 3,
          width: '20%',
          onChange(value, setValue) {
            setValue('license_plate_suffix', value.toUpperCase());
          },
        },
      ],
    },
    {
      name: 'vehicle_type_ulid',
      type: 'select',
      label: 'Tipe Kendaraan',
      placeholder: 'Pilih Tipe Kendaraan',
      required: true,
      onChangeCallback: (_value, option, setValue) => {
        setValue?.(
          'cubication',
          option?.cubic_capacity
            ? `${Intl.NumberFormat('id-ID', { style: 'decimal' }).format(option.cubic_capacity)}m³`
            : undefined
        );
      },
      ...vehicleTypeFormSelectProps,
    },
    {
      name: 'cubication',
      type: 'text',
      disabled: true,
      dependsOn: 'vehicle_type_ulid',
      label: 'Kubikasi',
      hideIfEmpty: true,
    },
    {
      name: 'kir_number',
      type: 'text',
      label: 'Nomor KIR Kendaraan',
      placeholder: 'Cth: 710293819029',
    },
    {
      name: 'kir_expired_date',
      type: 'date',
      label: 'Masa Berlaku Uji Berkala',
      placeholder: '01 Jan 2025',
    },
    {
      name: 'status',
      type: 'select',
      label: 'Status',
      placeholder: 'Pilih status',
      allowClear: false,
      required: true,
      options: [
        { label: 'Aktif', value: 'active' },
        { label: 'Tidak Aktif', value: 'inactive' },
      ],
    },
  ];

  // Handle form submission
  const handleSubmit = async (formValues: VehiclePayload, formMode: 'create' | 'update') => {
    const payload = {
      license_plate: `${formValues.license_plate_prefix}${formValues.license_plate_number}${formValues.license_plate_suffix}`,
      status: formValues.status!,
      vehicle_type_ulid: formValues.vehicle_type_ulid!,
      kir_expired_date: formValues.kir_expired_date ? dayjs(formValues.kir_expired_date).format('YYYY-MM-DD') : null,
      kir_number: formValues.kir_number,
    };

    if (formMode === 'create') {
      await createMutation.mutateAsync(payload);
      showNotification.success({
        message: `Berhasil membuat data kendaraan baru.`,
      });
      queryClient.invalidateQueries({ queryKey: ['vehicle'] });
    } else {
      await updateMutation.mutateAsync({ ...payload, ulid: selectedVehicle.ulid });
      showNotification.success({
        message: `Berhasil mengubah data kendaraan.`,
      });
      queryClient.invalidateQueries({ queryKey: ['vehicle'] });
    }
    resetFilters();
    hideModal();
  };

  // Handle create action
  const handleCreate = () => {
    setSelectedVehicle(FORM_VEHICLE_DEFAULT_VALUE);
    showModal('create');
  };

  // Handle update action
  const handleUpdate = (vehicle: Vehicle) => {
    // Normalize and parse license plate into prefix (letters), number (digits), suffix (letters)
    const normalizedPlate = vehicle.license_plate?.replace(/\s+/g, '');
    if (!normalizedPlate) return;

    const match = normalizedPlate.match(/^([A-Za-z]+)(\d+)([A-Za-z]+)$/);
    if (!match) return;

    const [, license_plate_prefix, license_plate_number, license_plate_suffix] = match;

    const initialValues: Vehicle = {
      ulid: vehicle.ulid,
      status: vehicle.status,
      license_plate_prefix: license_plate_prefix.toUpperCase(),
      license_plate_number,
      license_plate_suffix: license_plate_suffix.toUpperCase(),
      vehicle_type_ulid: vehicle.vehicle_type?.ulid!,
      kir_expired_date: vehicle.kir_expired_date ? (dayjs(vehicle.kir_expired_date!) as any) : undefined,
      kir_number: vehicle.kir_number || '',
      cubication: `${Intl.NumberFormat('id-ID', { style: 'decimal' }).format(vehicle.vehicle_type?.cubic_capacity!)}m³`,
    };
    setSelectedVehicle(initialValues);
    showModal('update');
  };

  return (
    <DataList
      title="Kendaraan"
      filterFields={[
        {
          type: 'search',
          name: 'license_plate',
          label: 'Cari kendaraan',
          placeholder: 'Cari nomor kendaraan',
          trigger: 'onChange',
        },
        {
          type: 'select',
          name: 'status',
          placeholder: 'Semua Status',
          label: 'Status',
          rounded: 'large',
          options: [
            { label: 'Aktif', value: 'active' },
            { label: 'Tidak Aktif', value: 'inactive' },
          ],
        },
        {
          type: 'select',
          name: 'vehicle_type',
          multiple: true,
          allowSearch: true,
          rounded: 'large',
          label: 'Tipe Kendaraan',
          placeholder: 'Tipe kendaraan',
          ...vehicleTypeFilterSelectProps,
        },
      ]}
    >
      <Flex gap={16} className="w-full h-full" vertical>
        <DataList.Header<VehicleListFilter>
          actions={{
            primary: {
              label: 'Kendaraan',
              onClick: handleCreate,
              icon: <PlusIcon size={16} />,
            },
            downloadUpload: {
              excelTemplate: {
                name: 'KENDARAAN',
                headers: [
                  {
                    header: 'Nomor Polisi Kendaraan',
                    key: 'license_plate',
                    example: 'D1234XYZ',
                    required: true,
                    width: 20,
                  },
                  {
                    header: 'Tipe Kendaraan',
                    key: 'vehicle_type_name',
                    reference: 'Tipe Kendaraan',
                    example: 'FUSO',
                    required: true,
                    width: 20,
                  },
                  {
                    header: 'Nomor KIR',
                    key: 'kir_number',
                    example: '148710238',
                    width: 20,
                  },
                  {
                    header: 'Masa Berlaku Uji Berkala',
                    key: 'kir_expired_date',
                    example: '30/01/2027',
                    isDate: true,
                    width: 20,
                  },
                  {
                    header: 'Status',
                    key: 'status',
                    reference: 'Status',
                    example: 'Aktif',
                    required: true,
                    width: 20,
                  },
                ],
                referenceData: {
                  ['Tipe Kendaraan']: options['vehicle-types-filter']
                    ?.sort((a, b) => (a.name as string).localeCompare(b.name as string))
                    .map(item => item.name),
                  ['Kubikasi']: options['vehicle-types-filter']?.map(item => `${item.cubic_capacity}m³`),
                  ['Status']: ['Aktif', 'Tidak Aktif'],
                },
              },
              upload: {
                url: '/vehicles/import',
                queryKey: 'vehicle',
              },
              download: {
                url: '/vehicles/export',
                params: filters,
              },
              resourceName: 'kendaraan',
            },
          }}
        />
        <DataList.List<Vehicle>
          tableSize={isMobile ? 'small' : undefined}
          useQuery={useVehicleList}
          emptyDesc="Coba gunakan tipe kendaraan atau status lainnya."
          columns={[
            {
              title: 'Nomor Polisi Kendaraan',
              dataIndex: 'license_plate',
              key: 'license_plate',
              fixed: !isMobile ? 'left' : undefined,
              width: 160,
            },
            {
              title: 'Tipe Kendaraan',
              dataIndex: 'vehicle_type',
              key: 'vehicle_type',
              render: (_, record) => {
                return record.vehicle_type?.name;
              },
            },
            {
              title: 'Kubikasi',
              dataIndex: 'cubic_capacity',
              key: 'cubication',
              render: (_, record) => {
                return (
                  <>{`${record.vehicle_type?.cubic_capacity ? `${Intl.NumberFormat('id-ID', { style: 'decimal' }).format(record.vehicle_type.cubic_capacity)}m³` : '-'}`}</>
                );
              },
            },
            {
              title: 'Nomor KIR',
              dataIndex: 'kir_number',
              key: 'kir_number',
            },
            {
              title: 'Masa Berlaku Uji Berkala',
              dataIndex: 'kir_expired_date',
              key: 'kir_expired_date',
              render: (_, record) => {
                return record?.kir_expired_date ? dayjs(record.kir_expired_date).format('DD MMM YYYY') : '';
              },
            },
            {
              title: 'Status',
              dataIndex: 'status',
              key: 'status',
              width: 140,
              align: 'center',
              render: (_, record) => {
                return (
                  <Tag
                    className={`rounded-full! mr-0! ${record.status === 'active' ? 'text-green-700! bg-green-100!' : 'text-red-700! bg-red-100!'}`}
                    bordered={false}
                  >
                    {record.status === 'active' ? 'Aktif' : 'Tidak Aktif'}
                  </Tag>
                );
              },
            },
            {
              title: 'Aksi',
              fixed: !isMobile ? 'right' : undefined,
              align: 'center',
              width: 150,
              render: (_, record) => {
                return (
                  <Button type="tertiary" size="middle" shape="round" onClick={() => handleUpdate(record)}>
                    Update Data
                  </Button>
                );
              },
            },
          ]}
        />

        {/* Form Modal for Create/Update */}
        <FormModal<VehiclePayload>
          visible={visible}
          mode={mode}
          title={mode === 'create' ? 'Tambah Data Kendaraan' : 'Update Data Kendaraan'}
          fields={vehicleFields}
          initialValues={selectedVehicle}
          validationSchema={vehicleSchema}
          onSubmit={handleSubmit}
          onCancel={hideModal}
          loading={createMutation.isPending || updateMutation.isPending}
        />
      </Flex>
    </DataList>
  );
};

export default VehiclePage;
